import React, { useState } from 'react';
import { Path, useForm } from 'react-hook-form';
import { Collapse } from 'reactstrap';
import BoldButton from '../Common/BoldButton';
import KaAlert from '../Common/KaAlert';
import KaModal from '../Common/KaModal';
import SpinnerButton from '../Common/SpinnerButton';
import { RowFormText } from '../Common/Forms/FormText';
import Toggle from '../Common/Toggle';
import useDisplayMessage from '../Common/useDisplayMessage';
import { PermissionField, RolePermissions, enabledPermissionCount, nonePermissions, permissions, pickPermissions, totalPermissionCount } from '../Common/permissions';
import { UserRoleDto } from '../Requests/useGetUsers';
import { useCreateUserRole, useUpdateUserRole } from '../Requests/useUpsertUserRole';

interface PermissionProps {
  flags: RolePermissions;
  setFlag: (field: PermissionField, value: boolean) => void;
}

interface ToggleProps extends PermissionProps {
  field: PermissionField;
  label: string;
  description?: string;
  enabledText?: string;
  disabledText?: string;
  viewHide?: boolean;
  hiddenWhenOff?: boolean;
  borderless?: boolean;
}

const PermissionToggle = ({ label, description, borderless = false, ...props }: ToggleProps) => {
  const on = props.flags[props.field];
  const onLabel = props.enabledText ?? 'Modify';
  const offLabel = props.disabledText ?? 'View only';
  const border = borderless ? '' : ' border-bottom';
  const descClass = description ? '' : ' pb-3';

  return <div className={`d-flex justify-content-between align-items-center py-2${border}${descClass}`} title={label}>
    <div className="me-3">
      <div className="fw-bold">{label}</div>
      {description && <div className="text-muted small">{description}</div>}
    </div>
    <div className="flex-shrink-0">
      <Toggle
        size="sm"
        offLabel={offLabel}
        onLabel={onLabel}
        value={on}
        onChange={next => props.setFlag(props.field, next)} />
    </div>
  </div>
};

interface GroupProps extends PermissionProps {
  label: string;
  options: { field: PermissionField; label: string }[];
}

const PermissionGroup = ({ label, options, flags, setFlag }: GroupProps) => {
  const [keptOpen, setKeptOpen] = useState(false);
  const anyChecked = options.some(({ field }) => flags[field]);
  const open = keptOpen || anyChecked;

  const toggleParent = () => {
    setKeptOpen(!anyChecked);
    options.forEach(({ field }) => setFlag(field, !anyChecked));
  };

  return <div className="pt-2 pb-3 border-bottom" title={label}>
    <div className="d-flex justify-content-between align-items-center">
      <div className="fw-bold me-3">{label}</div>
      <div className="flex-shrink-0">
        <Toggle
          size="sm"
          offLabel="View only"
          onLabel="Manage"
          value={anyChecked}
          onChange={next => { if (next !== anyChecked) toggleParent(); }} />
      </div>
    </div>
    <Collapse isOpen={open}>
      <div className="ms-4 pt-2">
        {options.map(({ field, label: optionLabel }) => (
          <div key={field} className="form-check">
            <input
              type="checkbox"
              id={field}
              className="form-check-input"
              checked={flags[field]}
              onChange={() => { setKeptOpen(true); setFlag(field, !flags[field]); }} />
            <label className="form-check-label" htmlFor={field}>{optionLabel}</label>
          </div>
        ))}
      </div>
    </Collapse>
  </div>
};

interface IProps {
  role?: UserRoleDto;
  onHide: () => void;
  onSuccess: (name: string) => void;
}

interface RoleForm extends RolePermissions {
  name: string;
  description: string;
}

const roleDefaults = (role?: UserRoleDto): RoleForm => ({
  name: role?.name ?? '',
  description: role?.description ?? '',
  ...(role ? pickPermissions(role) : nonePermissions()),
});

export const RoleModal = (props: IProps) => {
  const { role } = props;
  const isEdit = !!role;
  const displayMessage = useDisplayMessage();

  const { register, handleSubmit, getValues, setValue, watch, formState: { errors } } = useForm<RoleForm>({
    defaultValues: roleDefaults(role),
  });

  const flags = watch();

  const create = useCreateUserRole({
    onSuccess: () => props.onSuccess(getValues().name),
    onError: (e) => displayMessage.fail(e.message),
  });

  const update = useUpdateUserRole(role?.id ?? '', {
    onSuccess: () => props.onSuccess(getValues().name),
    onError: (e) => displayMessage.fail(e.message),
  });

  const submit = (form: RoleForm) => {
    if (isEdit) update.request(form); else create.request(form);
  };

  const hookProps = (name: Path<RoleForm>) => ({
    labelSpace: 3,
    inputSpace: 8,
    name,
    setValue,
    register,
    errors,
  });

  const toggleProps = { flags, setFlag: (field: PermissionField, value: boolean) => setValue(field, value) };

  return <KaModal
    size="lg"
    scrollable
    onHide={props.onHide}
    show
    title={isEdit ? `Edit ${role!.name}` : 'Create role'}
    body={(<>
      <KaAlert displayMessage={displayMessage.message} onClose={displayMessage.clear} />
      <RowFormText displayName="Name"
        {...hookProps('name')}
        options={{
          required: 'Name is required',
          validate: { length: v => v.length < 51 || 'Name cannot be longer than 50 characters' },
        }} />
      <RowFormText displayName="Description"
        {...hookProps('description')}
        options={{
          required: 'Description is required',
          validate: { length: v => v.length < 251 || 'Description cannot be longer than 250 characters' },
        }} />
      <div>
        <div>
          <div className="d-flex justify-content-between align-items-center border-bottom pb-1 mb-2 mt-4">
            <h3 className="ka-blue mb-0">Features</h3>
            <div className="d-flex align-items-center">
              <span className="text-muted small me-2">{enabledPermissionCount(flags)} of {totalPermissionCount} permissions enabled</span>
              <BoldButton className="btn-ghost-secondary btn-sm" onClick={() => permissions.forEach(field => setValue(field, true))}>Enable All</BoldButton>
            </div>
          </div>
          <PermissionToggle 
            field="dashboardReportsView" 
            label="Dashboard & Reports" 
            enabledText="Visible"
            disabledText="Hidden" 
            {...toggleProps} />
          <PermissionToggle field="productsModify" label="Products & Recipes" {...toggleProps} />
          <PermissionToggle field="customersModify" label="Customers" {...toggleProps} />
          <PermissionGroup label="Orders"
            options={[
              { field: 'ordersModify', label: 'Modify' },
              { field: 'ordersComplete', label: 'Complete' },
              { field: 'ordersVoidTransaction', label: 'Void Order Transactions' },
            ]}
            {...toggleProps} />
          <PermissionGroup label="Purchase Orders"
            options={[
              { field: 'purchaseOrderModify', label: 'Modify' },
              { field: 'purchaseOrderComplete', label: 'Complete Purchase Orders' },
            ]}
            {...toggleProps} />
          <PermissionToggle field="otherEntitiesModify" label="Other"
            description="Branches, Drivers, Transports, Carriers, Applicators, Suppliers" {...toggleProps} />
        </div>

        <div className="mb-4">
          <h3 className="ka-blue border-bottom pb-1 mb-2 mt-7">Administration</h3>
          <PermissionToggle 
            field="settingsModify" 
            label="Settings" 
            disabledText="Hidden"
            description="Data Sharing, Order Settings, PO Settings, Emails" 
            {...toggleProps} />
          <PermissionToggle 
            field="integrationsModify" 
            label="Integrations" 
            borderless 
            disabledText="Hidden"
            {...toggleProps} />
        </div>
      </div>
    </>)}
    footer={
      <SpinnerButton
        className="btn-ghost-primary"
        spinning={isEdit ? update.isLoading : create.isLoading}
        onClick={handleSubmit(submit)}>
        {isEdit ? 'Save' : 'Create'}
      </SpinnerButton>} />
};
